import { Reorder } from 'framer-motion';
import { GripVertical, ArrowUpDown } from 'lucide-react';
import React from 'react';

/**
 * DataGridTable - Vista de tabla optimizada para Desktop.
 */
const EMPTY_ARRAY = [];

const DataGridTable = ({
    columns,
    data,
    selectable,
    selectedIds = EMPTY_ARRAY,
    onSelectAll,
    onSelectRow = () => { },
    onReorder,
    reorderValues,
    onRowClick,
    getIndicatorColor,
    rowClassName,
    viewMode,
    className,
    hideIndicator = false,
    stickyOffset = '0px',
    sortField,
    sortDir,
    onSort
}) => {
    return (
        <div className={`hidden ${viewMode === 'grid' ? '' : 'md:flex'} flex-col min-w-0 w-full  ${className}`}>
            <div className="flex-1 overflow-x-auto pb-6 scrollbar-thin">
                <table className="w-full text-left border-separate border-spacing-y-[12px]">
                    <thead>
                        <tr className="text-secondary">
                            {onReorder && (
                                <th
                                    className="sticky z-[35] px-8 py-[26px] w-10 min-w-[40px] text-center bg-primary text-accent rounded-l-2xl border-y border-l border-primary"
                                    style={{ top: 0 }}
                                >
                                    <div className="flex items-center justify-center">
                                        <span className="sr-only">Arrastrar</span>
                                    </div>
                                </th>
                            )}
                            {columns.map((col, index) => {
                                const isFirst = index === 0 && !onReorder;
                                const isLast = index === columns.length - 1;
                                const isSortable = col.sortable && onSort;
                                const isActive = sortField === (col.sortKey || col.key);

                                return (
                                    <th
                                        key={col.key || index}
                                        onClick={() => isSortable && onSort(col.sortKey || col.key)}
                                        style={{
                                            top: 0,
                                            ...(col.width ? { width: col.width, minWidth: col.width } : {})
                                        }}
                                        className={`sticky z-[35] px-8 py-[26px] text-[9px] font-black uppercase tracking-[0.2em] bg-primary text-accent border-y border-primary whitespace-nowrap ${isFirst ? 'rounded-l-2xl border-l' : ''} ${isLast ? 'rounded-r-2xl border-r' : ''} ${col.headerClassName || ''} ${isSortable ? 'cursor-pointer select-none group' : ''}`}
                                    >
                                        <div className={`flex items-center gap-2 ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : ''}`}>
                                            <span className="truncate">{col.header}</span>
                                            {isSortable && (
                                                <ArrowUpDown 
                                                    size={12} 
                                                    className={`transition-all duration-300 ${isActive ? 'opacity-100 text-white' : 'opacity-20 group-hover:opacity-60'} ${isActive && sortDir === 'desc' ? 'rotate-180' : ''}`}
                                                />
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <Reorder.Group
                        as="tbody"
                        axis="y"
                        values={onReorder ? (reorderValues || data) : []}
                        onReorder={onReorder || (() => { })}
                    >
                        {data.map((item, index) => {
                            const isSelected = selectedIds.includes(item.id);
                            return (
                                <Reorder.Item
                                    as="tr"
                                    key={item.id}
                                    value={item}
                                    dragListener={!!onReorder}
                                    className={`group transition-all ${onReorder ? 'cursor-grab active:cursor-grabbing' : (onRowClick || selectable) ? 'cursor-pointer' : ''} ${rowClassName || ''}`}
                                    onClick={(e) => {
                                        if (e.target.closest('button, a, input, [role="button"]')) return;
                                        if (selectable && onSelectRow) {
                                            onSelectRow(item.id);
                                        }
                                        if (onRowClick) onRowClick(item, e);
                                    }}
                                >
                                    {onReorder && (
                                        <td className={`px-8 py-6 w-10 min-w-[40px] text-center transition-all duration-300 relative whitespace-nowrap border-y border-l rounded-l-2xl ${isSelected ? 'bg-[#1a1c1e] border-amber-500 text-amber-500 shadow-2xl shadow-amber-500/20' : 'bg-white border-slate-100/50 border-l-slate-100/50 text-gray-300 shadow-sm'}`}>
                                            {!hideIndicator && (
                                                <div className={`absolute left-0 top-3 bottom-3 w-1.5 transition-all rounded-r-full shadow-[2px_0_8px_rgba(0,0,0,0.1)] ${getIndicatorColor ? getIndicatorColor(item) : 'bg-slate-200 group-hover:bg-accent/40'
                                                    }`} />
                                            )}
                                            <div className="flex items-center justify-center">
                                                <GripVertical size={18} />
                                            </div>
                                        </td>
                                    )}
                                    {columns.map((col, idx) => {
                                        const isFirst = idx === 0 && !onReorder;
                                        const isLast = idx === columns.length - 1;
                                        return (
                                            <td
                                                key={col.key || idx}
                                                style={col.width ? { width: col.width, minWidth: col.width } : {}}
                                                className={`px-8 py-5 transition-all duration-300 border-y relative ${isFirst ? 'border-l rounded-l-2xl' : ''} ${isLast ? 'border-r rounded-r-2xl' : ''} ${isSelected ? 'bg-[#1a1c1e] border-amber-500 text-white shadow-2xl shadow-amber-500/20' : 'bg-white border-slate-100 text-slate-900 shadow-sm'} ${col.cellClassName || ''} ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}
                                            >
                                                {isFirst && !hideIndicator && !onReorder && (
                                                    <div className={`absolute left-0 top-3 bottom-3 w-1.5 transition-all rounded-r-full shadow-[2px_0_8px_rgba(0,0,0,0.1)] ${getIndicatorColor ? getIndicatorColor(item) : (isSelected ? 'bg-amber-500' : 'bg-slate-200 group-hover:bg-accent/40')
                                                        }`} />
                                                )}
                                                <div className={`transition-colors duration-300 ${isSelected ? 'brightness-125' : ''}`}>
                                                    {col.render ? col.render(item, index, isSelected) : item[col.key]}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </Reorder.Item>
                            );
                        })}
                    </Reorder.Group>
                </table>
            </div>
        </div>
    );
};

export default React.memo(DataGridTable);
